import apiClient from "../../services/apiClient";
import { VocabularyData } from "../types";
import { v4 as uuidv4 } from "uuid";
import { mobileAppUri, dataLoaderUri } from "../API";

// --- Fetch Sections ---
export const fetchSections = async (
  targetLanguageCode: string
): Promise<string[]> => {
  const endpoint = `${mobileAppUri}/api/v1/all-sections?Targ_Lang_Code=${targetLanguageCode}`;
  console.log(`Workspaceing sections from: ${endpoint}`);
  try {
    const response = await apiClient.get(endpoint);

    // Log raw response for debugging
    console.log(
      `Raw response data for Targ_Lang_Code=${targetLanguageCode}:`,
      JSON.stringify(response.data, null, 2)
    );

    // Check if the expected 'sections' property exists and is a string
    if (!response || typeof response.sections !== "string") {
      console.warn(
        `API response for ${targetLanguageCode} did not contain a 'sections' string:`,
        response
      );
      return []; // Return empty if structure is not as expected
    }

    let parsedSections: any[];
    try {
      // Attempt to parse the stringified JSON array
      parsedSections = JSON.parse(response.sections);
      if (!Array.isArray(parsedSections)) {
        console.warn(
          `Parsed 'sections' string for ${targetLanguageCode} did not result in an array:`,
          parsedSections
        );
        return []; // Return empty if parsing doesn't yield an array
      }
    } catch (parseError) {
      console.error(
        `Error parsing 'sections' string for ${targetLanguageCode}:`,
        parseError,
        "String was:",
        response.sections
      );
      return []; // Return empty on parsing error
    }

    console.log(
      `Successfully parsed 'sections' for ${targetLanguageCode}:`,
      parsedSections
    );

    // Extract section identifiers
    const allPotentialSections: string[] = [];
    parsedSections.forEach((section: any) => {
      // Always try to add the main section Identifier if it exists
      if (section.Identifier) {
        allPotentialSections.push(String(section.Identifier));
      }

      // Check if Lessons exists, might be a stringified array or an actual array
      let parsedLessons: any[] = [];
      if (typeof section.Lessons === "string") {
        try {
          parsedLessons = JSON.parse(section.Lessons);
          if (!Array.isArray(parsedLessons)) {
            console.warn(
              `Parsed Lessons string for section ${section.Identifier} was not an array.`
            );
            parsedLessons = []; // Default to empty if not an array
          }
        } catch (lessonParseError) {
          console.warn(
            `Error parsing Lessons string for section ${section.Identifier}:`,
            lessonParseError,
            "String was:",
            section.Lessons
          );
          parsedLessons = []; // Default to empty on error
        }
      } else if (Array.isArray(section.Lessons)) {
        // Handle case where API might return an actual array directly
        parsedLessons = section.Lessons;
      } else {
        // Lessons field might be missing or null/undefined
        // console.log(`No valid Lessons array/string found for section ${section.Identifier}`);
      }

      // Add ReviewSection from the (potentially parsed) Lessons array
      parsedLessons.forEach((lesson: any) => {
        if (lesson.ReviewSection) {
          allPotentialSections.push(String(lesson.ReviewSection));
        }
      });
    });

    // Filter out empty/null strings and ensure uniqueness
    const uniqueSections = Array.from(
      new Set(allPotentialSections.filter((s) => s && String(s).trim() !== ""))
    );

    console.log(
      `Extracted unique sections for ${targetLanguageCode}:`,
      uniqueSections
    );
    return uniqueSections;
  } catch (error: any) {
    // Log detailed error information
    const status = error.response?.status;
    const errorData = error.response?.data;
    console.error(
      `Error fetching sections for ${targetLanguageCode} (Status: ${
        status || "N/A"
      }):`,
      errorData || error.message || error
    );
    return []; // Return empty array on error to prevent component crash
  }
};

// --- Fetch Vocabulary Words ---
export const fetchVocabularyWords = async (
  section: string,
  targetLanguage: string = "ES",
  baseLanguage?: string  // Made optional - if not provided, fetches ALL base languages
): Promise<VocabularyData[]> => {
  const endpoint = `${mobileAppUri}/api/v1/vocabulary`;
  
  // Build params conditionally
  const params: any = {
    section: section,
    Targ_Lang_Code: targetLanguage
  };
  
  // Only add courseId if baseLanguage is specified
  if (baseLanguage) {
    params.courseId = baseLanguage; // Backend uses courseId for base language filtering
    console.log(
      `Fetching vocabulary for section "${section}", target: "${targetLanguage}", base: "${baseLanguage}" from: ${endpoint}`
    );
  } else {
    console.log(
      `Fetching vocabulary for section "${section}", target: "${targetLanguage}", ALL base languages from: ${endpoint}`
    );
  }
  
  try {
    const response = await apiClient.get(endpoint, { params });

    console.log(
      `Raw vocabulary response for section "${section}":`,
      response
    );

    let items: any[];

    // Determine where the items array is and parse if necessary
    if (Array.isArray(response)) {
      items = response; // Response is directly the array
    } else if (response && Array.isArray(response.items)) {
      items = response.items; // Response is an object with an 'items' array
    } else if (response && typeof response.items === "string") {
      // Response is an object with 'items' as a stringified JSON array
      try {
        items = JSON.parse(response.items);
        if (!Array.isArray(items)) {
          console.warn(
            `Parsed vocabulary 'items' string for section "${section}" was not an array.`
          );
          items = [];
        }
      } catch (e) {
        console.error(
          `Error parsing vocabulary 'items' string for section "${section}":`,
          e
        );
        items = [];
      }
    } else {
      console.warn(
        `Vocabulary response for section "${section}" lacks a valid array or 'items' property:`,
        response
      );
      return []; // Return empty if no valid items array found
    }

    if (items.length > 0) {
      console.log(
        `First 3 raw items for section "${section}":`,
        JSON.stringify(items.slice(0, 3), null, 2)
      );
    } else {
      console.log(`No vocabulary items found for section "${section}".`);
      return []; // Return early if no items
    }

    console.log(`📊 [FETCH DEBUG] Raw items count: ${items.length}`);

    // Map raw items to VocabularyData structure
    const mappedWords = items
      .map((item: any, index: number): VocabularyData | null => {
        if (index < 3) {
          console.log(`📊 [FETCH DEBUG] Processing item ${index}:`, {
            word_id: item.word_id?.S || item.word_id,
            lang_pair_word_id: item.lang_pair_word_id?.S || item.lang_pair_word_id,
            targ_word: item.targ_word?.S || item.targ_word,
            base_word: item.base_word?.S || item.base_word
          });
        }
        // Attempt to extract essential fields first (updated for new schema)
        let identifier = null;

        // Check if we have the new schema's lang_pair_word_id
        const langPairWordId = item.lang_pair_word_id?.S || item.lang_pair_word_id;

        // IMPORTANT: Use the FULL lang_pair_word_id as the identifier to ensure uniqueness
        // across different language pair combinations of the same word.
        // The lang_pair_word_id format is: #Targ_ES#Base_DE#word_id
        // This is unique for each target/base language combination
        // NOTE: We keep it as-is (encoded or not) because the database might have
        // both encoded and non-encoded versions, and decoding would create duplicates
        if (langPairWordId) {
          identifier = langPairWordId;
        }

        // Fall back to other identifier fields if lang_pair_word_id is not available
        if (!identifier) {
          identifier =
            item.word_id?.S ||  // New schema might have word_id separately
            item.Identifier?.S ||
            item.identifier?.S ||
            item.id?.S ||
            item.word_id ||
            item.Identifier ||
            item.identifier ||
            item.id ||
            null;
        }
        const targWord =
          item.targ_word?.S ||           // New schema field name
          item.Targ_Word?.S ||
          item.target_word?.S ||
          item.Target_Word?.S ||
          item.targ_word ||
          item.Targ_Word ||
          item.target_word ||
          item.Target_Word ||
          null;
        const baseWord =
          item.base_word?.S ||           // New schema field name
          item.Base_Word?.S ||
          item.base_Word?.S ||
          item.BaseWord?.S ||
          item.base_word ||
          item.Base_Word ||
          item.base_Word ||
          item.BaseWord ||
          null;

        // Skip item if essential identifying information is missing
        if (!identifier && !targWord && !baseWord) {
          console.warn(
            `Skipping item in section "${section}" due to missing Identifier, Targ_Word, and Base_Word:`,
            item
          );
          return null;
        }

        // Proceed with mapping, providing fallbacks (updated for new schema)
        // Try to get language codes from direct fields first
        let baseLangCode = item.base_lang_code?.S ||      // New schema field name
          item.Base_Lang_Code?.S ||
          item.base_lang_code ||
          item.Base_Lang_Code ||
          null;  // Use null instead of empty string for better falsy checking

        let targLangCode = item.targ_lang_code?.S ||      // New schema field name
          item.Targ_Lang_Code?.S ||
          item.targ_lang_code ||
          item.Targ_Lang_Code ||
          null;  // Use null instead of empty string for better falsy checking

        // If language codes are missing, try to extract from lang_pair_word_id
        if ((!baseLangCode || !targLangCode) && langPairWordId) {
          console.log(`🔍 Attempting to extract language codes from lang_pair_word_id: ${langPairWordId}`);

          // Format: #Targ_ES#Base_DE#word_id
          const parts = langPairWordId.split('#').filter((p: string) => p);
          console.log(`   Split into parts:`, parts);

          parts.forEach((part: string) => {
            if (part.startsWith('Targ_')) {
              const extracted = part.replace('Targ_', '').trim();
              if (extracted && !targLangCode) {  // Only set if not already set and extracted is non-empty
                targLangCode = extracted;
                console.log(`   ✓ Extracted Targ: ${extracted}`);
              }
            } else if (part.startsWith('Base_')) {
              const extracted = part.replace('Base_', '').trim();
              if (extracted && !baseLangCode) {  // Only set if not already set and extracted is non-empty
                baseLangCode = extracted;
                console.log(`   ✓ Extracted Base: ${extracted}`);
              }
            }
          });

          if (baseLangCode && targLangCode) {
            console.log(`✅ Successfully extracted language codes for ${targWord}: Targ=${targLangCode}, Base=${baseLangCode}`);
          } else {
            console.warn(`⚠️ Extraction incomplete for ${targWord}:`, { baseLangCode, targLangCode, langPairWordId });
          }
        }

        // Final validation - warn if language codes are still missing after extraction attempt
        if (!baseLangCode || !targLangCode) {
          console.error(`❌ Vocabulary item STILL missing language codes after extraction:`, {
            identifier,
            targWord,
            baseWord,
            Base_Lang_Code: baseLangCode,
            Targ_Lang_Code: targLangCode,
            lang_pair_word_id: langPairWordId,
            hasLangPairWordId: !!langPairWordId,
            rawItem: JSON.stringify(item).substring(0, 200) + '...'
          });
        }

        const word: VocabularyData = {
          Identifier: identifier || `temp-${uuidv4()}`, // Use UUID for truly missing IDs
          Level:
            item.level?.S ||              // New schema field name
            item.Level?.S ||
            item.level ||
            item.Level ||
            section, // Default Level to section if missing
          Base_Word: baseWord || "",
          Base_Lang_Code: baseLangCode || "",  // Use empty string as fallback for type compatibility
          Base_Lang_Options:
            item.base_lang_options?.S ||   // New schema field name
            item.Base_Lang_Options?.S ||
            item.base_lang_options ||
            item.Base_Lang_Options ||
            "[]",
          Targ_Word: targWord || "",
          Targ_Lang_Code: targLangCode || "",  // Use empty string as fallback for type compatibility
          Targ_Lang_Options:
            item.targ_lang_options?.S ||   // New schema field name
            item.Targ_Lang_Options?.S ||
            item.targ_lang_options ||
            item.Targ_Lang_Options ||
            "[]",
          Explanation_Word_Timing:
            item.explanation_word_timing?.S ||  // New schema field name
            item.Explanation_Word_Timing?.S ||
            item.explanation_word_timing ||
            item.Explanation_Word_Timing ||
            "",
          Phonetic_Transcription:
            item.phonetic_transcription?.S ||   // New schema field name
            item.Phonetic_Transcription?.S ||
            item.phonetic_transcription ||
            item.Phonetic_Transcription ||
            "",
          Pronunciation_Explanation:
            item.pronunciation_explanation?.S || // New schema field name
            item.Pronunciation_Explanation?.S ||
            item.pronunciation_explanation ||
            item.Pronunciation_Explanation ||
            "",
          Pronunciation_Explanation_Audio:
            item.pronunciation_explanation_audio?.S || // New schema field name
            item.Pronunciation_Explanation_Audio?.S ||
            item.pronunciation_explanation_audio ||
            item.Pronunciation_Explanation_Audio ||
            "",
          Targ_Syllable:
            item.targ_syllable?.S ||            // New schema field name
            item.Targ_Syllable?.S ||
            item.targ_syllable ||
            item.Targ_Syllable ||
            "",
          Targ_Syllable_Sounds:
            item.targ_syllable_sounds?.S ||     // New schema field name
            item.Targ_Syllable_Sounds?.S ||
            item.targ_syllable_sounds ||
            item.Targ_Syllable_Sounds ||
            "",
          Word_Audio:
            item.word_audio?.S ||               // New schema field name
            item.Word_Audio?.S ||
            item.word_audio ||
            item.Word_Audio ||
            "",
          ImageURL:
            item.image_url?.S ||                // New schema field name
            item.ImageURL?.S ||
            item.ImageUrl?.S ||
            item.image_url ||
            item.ImageURL ||
            item.ImageUrl ||
            "",
          Word_Order:
            item.word_order?.N !== undefined ? Number(item.word_order.N) :  // New schema field name (Number type)
            item.Word_Order?.N !== undefined ? Number(item.Word_Order.N) :
            item.word_order !== undefined ? Number(item.word_order) :
            item.Word_Order !== undefined ? Number(item.Word_Order) :
            undefined,  // Optional field - undefined if not set
        };
        return word;
      })
      .filter((word): word is VocabularyData => word !== null); // Filter out nulls (skipped items)

    console.log(
      `Successfully mapped ${mappedWords.length} vocabulary words for section "${section}".`
    );

    // Check for duplicate identifiers in the mapped words
    const identifierCounts = new Map<string, number>();
    mappedWords.forEach((word) => {
      const count = identifierCounts.get(word.Identifier) || 0;
      identifierCounts.set(word.Identifier, count + 1);
    });

    const duplicateIds = Array.from(identifierCounts.entries())
      .filter(([_, count]) => count > 1)
      .map(([id, count]) => ({ id, count }));

    if (duplicateIds.length > 0) {
      console.warn(`⚠️ [FETCH DEBUG] Found ${duplicateIds.length} duplicate identifiers in mapped words:`, duplicateIds);
      console.warn(`⚠️ [FETCH DEBUG] This suggests the API is returning duplicate items or mapping is creating duplicates`);
    } else {
      console.log(`✅ [FETCH DEBUG] No duplicate identifiers found in ${mappedWords.length} mapped words`);
    }

    return mappedWords;
  } catch (error: any) {
    const status = error.response?.status;
    const errorData = error.response?.data;
    console.error(
      `Error fetching vocabulary for section "${section}" (Status: ${
        status || "N/A"
      }):`,
      errorData || error.message || error
    );
    return []; // Return empty array on error
  }
};

// --- Image Upload (Using Presigned URL) ---
// Assuming this logic is correct based on your backend setup
export const uploadImageAndGetUrl = async (
  croppedBlob: Blob,
  imageName: string // e.g., "word_timestamp.png"
): Promise<string> => {
  // Return the final URL
  const endpoint = `${dataLoaderUri}/api/v1/upload-vocab-image`;
  console.log(`Requesting presigned URL from: ${endpoint} for ${imageName}`);
  try {
    // 1. Get Presigned URL from your backend
    const presignedUrlData = await apiClient.post(endpoint, {
      fileName: imageName,
      fileType: croppedBlob.type,
    });
    const presignedUrl = presignedUrlData.url; // Adjust if the key name is different

    if (!presignedUrl) {
      throw new Error("Presigned URL not found in response data.");
    }

    console.log(`Received presigned URL. Uploading image to S3...`);

    // 2. Upload the image data (Blob) to the Presigned URL (directly to S3)
    const uploadResponse = await fetch(presignedUrl, {
      method: "PUT",
      headers: { "Content-Type": croppedBlob.type }, // S3 needs the correct content type
      body: croppedBlob,
    });

    if (!uploadResponse.ok) {
      // S3 often returns XML error details
      const errorText = await uploadResponse.text();
      throw new Error(
        `Failed to upload image to presigned URL (Status: ${uploadResponse.status}): ${errorText}`
      );
    }

    // 3. Return the base URL (without query parameters)
    const finalUrl = presignedUrl.split("?")[0];
    console.log(`Image uploaded successfully. Final URL: ${finalUrl}`);
    return finalUrl;
  } catch (error: any) {
    console.error("Error during image upload process:", error);
    throw error; // Re-throw the error to be handled by the calling component
  }
};

// --- Add New Vocabulary Entry ---
// NOTE: This uses dataloaderBackend which may need schema updates for optimized vocabulary table
export const addVocabulary = async (data: VocabularyData): Promise<any> => {
  // Return type depends on API response
  const endpoint = `${dataLoaderUri}/api/v1/add-vocabulary`;
  console.log(`Adding vocabulary via: ${endpoint}`, data);
  try {
    const response = await apiClient.post(endpoint, data);
    console.log("Add vocabulary response:", response);
    return response; // Return response data
  } catch (error: any) {
    const status = error.response?.status;
    const errorData = error.response?.data;
    console.error(
      `Error adding vocabulary (Status: ${status || "N/A"}):`,
      errorData || error.message || error
    );
    throw error; // Re-throw error
  }
};

// --- Update Existing Vocabulary Entry ---
// NOTE: This uses dataloaderBackend which may need schema updates for optimized vocabulary table
export const updateVocabulary = async (
  identifier: string,
  level: string,
  updatedData: Partial<VocabularyData> // Allow partial updates
): Promise<any> => {
  // Debug logging to check what we're receiving
  console.log("updateVocabulary called with:", {
    identifier,
    level,
    hasIdentifier: !!identifier,
    hasLevel: !!level,
    identifierType: typeof identifier,
    levelType: typeof level,
    targLangCode: updatedData.Targ_Lang_Code,
    baseLangCode: updatedData.Base_Lang_Code
  });

  // Check if identifier and level are valid
  if (!identifier || !level) {
    console.error("Missing identifier or level for update!", { identifier, level });
    throw new Error(`Cannot update vocabulary: missing ${!identifier ? 'identifier' : 'level'}`);
  }

  // Validate language codes are present and non-empty
  const targLangCode = updatedData.Targ_Lang_Code?.trim();
  const baseLangCode = updatedData.Base_Lang_Code?.trim();

  if (!targLangCode || !baseLangCode) {
    const errorMsg = `Cannot update vocabulary: missing required language codes (Targ: ${targLangCode || 'MISSING'}, Base: ${baseLangCode || 'MISSING'})`;
    console.error(`❌ ${errorMsg}`, { identifier, level, updatedData });
    throw new Error(errorMsg);
  }

  // Extract word_id from lang_pair_word_id if needed
  // If identifier is in format "#Targ_ES#Base_DE#word_id", extract just the word_id
  let wordId = identifier;
  if (identifier.includes('#')) {
    const parts = identifier.split('#').filter((p: string) => p);
    const extractedWordId = parts.find((p: string) => !p.startsWith('Targ_') && !p.startsWith('Base_')) || identifier;

    // ALWAYS decode the word_id so backend stores it in human-readable form
    // This ensures consistency - all new/updated entries will use decoded format
    // Old encoded entries will remain until cleaned up via duplicate modal
    try {
      wordId = decodeURIComponent(extractedWordId);
      console.log(`📝 Extracted and decoded word_id "${wordId}" from "${extractedWordId}"`);
    } catch (e) {
      // If decoding fails (already decoded), use as-is
      wordId = extractedWordId;
      console.log(`📝 Extracted word_id "${wordId}" (already decoded)`);
    }
  }

  // Note: Ensure your API endpoint structure matches this pattern
  const endpoint = `${dataLoaderUri}/api/v1/update-vocabulary/${encodeURIComponent(wordId)}/${encodeURIComponent(level)}`;

  // Ensure required fields are in the request body for the new schema
  const requestBody = {
    ...updatedData,
    Identifier: wordId,  // Use extracted word_id, not full lang_pair_word_id
    Level: level,
    // Ensure Targ_Lang_Code and Base_Lang_Code are included with validated values
    Targ_Lang_Code: targLangCode,
    Base_Lang_Code: baseLangCode,
  };

  console.log(`📤 [UPDATE DEBUG] Full identifier (lang_pair_word_id): "${identifier}"`);
  console.log(`📤 [UPDATE DEBUG] Extracted word_id for API call: "${wordId}"`);
  console.log(`📤 [UPDATE DEBUG] Has % characters: ${wordId.includes('%')}`);
  console.log(
    `📤 [UPDATE DEBUG] Updating vocabulary via: ${endpoint}`,
    requestBody
  );
  try {
    const response = await apiClient.put(endpoint, requestBody);
    console.log("Update vocabulary response:", response);
    return response;
  } catch (error: any) {
    const status = error.response?.status;
    const errorData = error.response?.data;
    console.error(
      `Error updating vocabulary ${identifier}/${level} (Status: ${
        status || "N/A"
      }):`,
      errorData || error.message || error
    );
    throw error;
  }
};

// --- Delete Vocabulary Entry ---
// NOTE: Updated for new optimized vocabulary table schema - requires language codes
export const deleteVocabulary = async (
  identifier: string,
  level: string,
  languageCodes?: { Base_Lang_Code: string; Targ_Lang_Code: string }
): Promise<any> => {
  // Extract word_id from lang_pair_word_id if needed
  let wordId = identifier;
  if (identifier.includes('#')) {
    const parts = identifier.split('#').filter((p: string) => p);
    const extractedWordId = parts.find((p: string) => !p.startsWith('Targ_') && !p.startsWith('Base_')) || identifier;

    // ALWAYS decode the word_id for consistency
    try {
      wordId = decodeURIComponent(extractedWordId);
      console.log(`📝 [DELETE] Extracted and decoded word_id "${wordId}" from "${extractedWordId}"`);
    } catch (e) {
      wordId = extractedWordId;
      console.log(`📝 [DELETE] Extracted word_id "${wordId}" (already decoded)`);
    }
  }

  // Note: New schema requires language codes to construct the sort key
  const endpoint = `${dataLoaderUri}/api/v1/delete-vocabulary/${encodeURIComponent(wordId)}/${encodeURIComponent(level)}`;
  console.log(`Deleting vocabulary (${identifier}/${level}) via: ${endpoint}`);

  // For new schema, we need to send language codes in the request body
  const requestBody = languageCodes ? {
    Identifier: wordId,  // Use extracted word_id
    Level: level,
    Base_Lang_Code: languageCodes.Base_Lang_Code,
    Targ_Lang_Code: languageCodes.Targ_Lang_Code
  } : {};

  console.log("Delete request body:", requestBody);
  
  try {
    const response = await apiClient.request({
      method: 'delete',
      url: endpoint,
      data: requestBody
    });
    console.log("Delete vocabulary response:", response);
    return response;
  } catch (error: any) {
    const status = error.response?.status;
    const errorData = error.response?.data;
    console.error(
      `Error deleting vocabulary ${identifier}/${level} (Status: ${
        status || "N/A"
      }):`,
      errorData || error.message || error
    );
    throw error;
  }
};
